import React, { useCallback, useEffect, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    Input,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Spinner,
} from "reactstrap";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";

import { fetchAccountsByGroup } from "../../../api/requests/account-requests";
import {
    ACCOUNT_TYPE_BADGE_COLORS,
    ACCOUNT_TYPE_LABEL_MAP,
} from "../../../constants/accountTypes";

const PAGE_SIZES = [10, 25, 50, 100];

const styles = `
  .group-accounts-modern .table thead tr { background-color: #f9f9f9; }
  .group-accounts-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
  .group-accounts-modern .table td { vertical-align: middle; }
  .group-accounts-modern .table-hover tbody tr:hover { background: #fcfcfd; }
  .group-accounts-modern .btn-rounded { border-radius: 999px; }
  .group-accounts-modern .pill-input { border-radius: 999px !important; padding: .45rem .9rem; }
`;

const AccountTypeBadge = ({ type }) => (
    <span
        className="badge border-0"
        style={{
            borderRadius: 999,
            padding: ".35rem .5rem",
            backgroundColor: ACCOUNT_TYPE_BADGE_COLORS[type] || "#B0B7C3",
            color: "#fff",
        }}
        title={ACCOUNT_TYPE_LABEL_MAP[type] || type}
    >
        {ACCOUNT_TYPE_LABEL_MAP[type] || type}
    </span>
);

/**
 * GroupAccountsModal – Bir taksit grubuna bağlı carileri salt okunur listeler.
 *
 * Props
 * @param {boolean} isOpen        – Modal açık mı?
 * @param {Function} toggle       – Modal'ı kapat/aç
 * @param {object|null} group     – { id, groupName, accountCount }
 */
const GroupAccountsModal = ({ isOpen, toggle, group }) => {
    const [accounts, setAccounts] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const groupId = group?.id;

    // Modal her açıldığında filtreleri sıfırla
    useEffect(() => {
        if (isOpen) {
            setPage(0);
            setSearchTerm("");
            setDebouncedSearch("");
            setError(null);
        }
    }, [isOpen, groupId]);

    // Arama girişini geciktir (her tuşta istek atmamak için)
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPage(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const loadAccounts = useCallback(async () => {
        if (!isOpen || !groupId) return;
        setLoading(true);
        setError(null);
        try {
            const json = await fetchAccountsByGroup({
                groupId,
                page,
                size,
                searchTerm: debouncedSearch,
            });
            const data = json?.data || {};
            setAccounts(Array.isArray(data.content) ? data.content : []);
            setTotalElements(data.totalElements || 0);
            setTotalPages(data.totalPages || 0);
        } catch (e) {
            setAccounts([]);
            setTotalElements(0);
            setTotalPages(0);
            setError(e.message || "Bağlı cariler yüklenemedi");
        } finally {
            setLoading(false);
        }
    }, [isOpen, groupId, page, size, debouncedSearch]);

    useEffect(() => {
        loadAccounts();
    }, [loadAccounts]);

    // Sayfa numarası butonları (maks 5)
    const pageNumbers = () => {
        if (totalPages <= 1) return [];
        const start = Math.max(0, Math.min(page - 2, totalPages - 5));
        return Array.from({ length: Math.min(5, totalPages) }, (_, i) => start + i);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
            <style>{styles}</style>
            <ModalHeader toggle={toggle}>
                <span className="d-flex align-items-center gap-2">
                    <i className="bx bx-group" />
                    <span>
                        Bağlı Cariler
                        {group?.groupName && (
                            <span className="text-muted fw-normal"> — {group.groupName}</span>
                        )}
                    </span>
                </span>
            </ModalHeader>

            <ModalBody className="group-accounts-modern">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <Badge
                        className="px-3 py-2"
                        style={{ borderRadius: 999, backgroundColor: "#221C5D" }}
                    >
                        Toplam {totalElements} cari
                    </Badge>
                    <Input
                        type="text"
                        className="pill-input"
                        style={{ maxWidth: 260 }}
                        placeholder="Cari ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {error && (
                    <Alert color="danger" className="rounded-4">
                        <i className="bx bx-error-circle me-2" />
                        {error}
                        <button
                            className="btn btn-sm btn-outline-danger ms-2 btn-rounded"
                            onClick={loadAccounts}
                        >
                            Tekrar Dene
                        </button>
                    </Alert>
                )}

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <div className="text-muted mt-2">Cariler yükleniyor...</div>
                    </div>
                ) : (
                    <>
                        <div className="table-responsive">
                            <Table className="table align-middle table-hover mb-0">
                                <Thead>
                                    <Tr>
                                        <Th className="d-none d-sm-table-cell">Cari Kodu</Th>
                                        <Th>Cari/Bayi Adı</Th>
                                        <Th className="d-none d-md-table-cell">Tip</Th>
                                        <Th>Durum</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {accounts.length ? (
                                        accounts.map((a) => (
                                            <Tr key={a.id} className="border-bottom">
                                                <Td className="d-none d-sm-table-cell">
                                                    <span className="fw-semibold text-dark">
                                                        {a.accountCode}
                                                    </span>
                                                </Td>
                                                <Td>
                                                    <span className="fw-semibold text-dark">
                                                        {a.accountName}
                                                    </span>
                                                    {a.title && (
                                                        <div className="text-muted small">{a.title}</div>
                                                    )}
                                                </Td>
                                                <Td className="d-none d-md-table-cell">
                                                    <AccountTypeBadge type={a.accountType} />
                                                </Td>
                                                <Td>
                                                    <span
                                                        className="badge border-0"
                                                        style={{
                                                            borderRadius: 999,
                                                            backgroundColor: a.enabled ? "#71C30B" : "#E07A7A",
                                                            color: "#fff",
                                                        }}
                                                    >
                                                        {a.enabled ? "Aktif" : "Pasif"}
                                                    </span>
                                                </Td>
                                            </Tr>
                                        ))
                                    ) : (
                                        <Tr>
                                            <Td colSpan="4" className="text-center py-4">
                                                <div className="text-muted">
                                                    <i className="bx bx-user-x display-4 d-block mb-2" />
                                                    {debouncedSearch
                                                        ? "Aramanızla eşleşen cari bulunamadı"
                                                        : "Bu taksit grubuna bağlı cari yok"}
                                                    {!debouncedSearch && (
                                                        <div className="mt-2">
                                                            <small>
                                                                Cari kaydını düzenleyerek bu gruba atayabilirsiniz.
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            </Td>
                                        </Tr>
                                    )}
                                </Tbody>
                            </Table>
                        </div>

                        {totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                                <div className="d-flex align-items-center gap-2">
                                    <small className="text-muted">Sayfa başına</small>
                                    <Input
                                        type="select"
                                        bsSize="sm"
                                        style={{ width: 80, borderRadius: 999 }}
                                        value={size}
                                        onChange={(e) => {
                                            setSize(Number(e.target.value));
                                            setPage(0);
                                        }}
                                    >
                                        {PAGE_SIZES.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </Input>
                                </div>

                                <div className="d-flex align-items-center gap-1">
                                    <Button
                                        size="sm"
                                        color="light"
                                        className="btn-rounded"
                                        disabled={page === 0}
                                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                                    >
                                        <i className="bx bx-chevron-left" />
                                    </Button>
                                    {pageNumbers().map((p) => (
                                        <Button
                                            key={p}
                                            size="sm"
                                            className="btn-rounded"
                                            color={p === page ? "primary" : "light"}
                                            onClick={() => setPage(p)}
                                        >
                                            {p + 1}
                                        </Button>
                                    ))}
                                    <Button
                                        size="sm"
                                        color="light"
                                        className="btn-rounded"
                                        disabled={page >= totalPages - 1}
                                        onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                                    >
                                        <i className="bx bx-chevron-right" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </ModalBody>

            <ModalFooter className="border-0 pt-0">
                <Button color="secondary" outline className="btn-rounded" onClick={toggle}>
                    Kapat
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default GroupAccountsModal;
